import { ref, computed, watch, onBeforeMount, onMounted, onBeforeUnmount } from 'vue';
import { CommunicationUtil } from '@samsung360vr/overlay-app-sdk';
import { useTrackerStore } from '@/stores/tracker';
import { useBreakStore } from '@/stores/break';
import { BreakModeEnum } from '@/utils/dataAnalyzers';
import { logDebug, logError, logInfo, logWarn } from '@/utils/logger';
import { sendOverlayError, OVERLAY_ERROR } from '@/utils/nacxMetrics';

/**
 * Fires SLOT_DURATION_BELOW_THRESHOLD if the remaining slot time is too short
 * to display the overlay. Exported for unit testing.
 */
export function checkSlotDuration(remainingMs: number, minimumRequiredMs: number): boolean {
  if (remainingMs < minimumRequiredMs) {
    const msg = `Slot too short to show overlay: ${remainingMs}ms available, ${minimumRequiredMs}ms required`;
    sendOverlayError(OVERLAY_ERROR.SLOT_DURATION_BELOW_THRESHOLD, msg);
    return false;
  }
  return true;
}

/**
 * Fires FADE_OUT_SCREEN_NOT_REACHED if the overlay was shown but never
 * completed — i.e. it was torn down before reaching its terminal 'dismiss'
 * screen (the fade-out transition). Named around "fade out", not "dismiss",
 * so it isn't confused with the dismiss-button flow (markDismissed /
 * nacxOverlayDismissed) — this fires regardless of whether the user ever
 * interacted with a dismiss button.
 * Exported for unit testing.
 */
export function checkFadeOutReached(
  shouldShow: boolean,
  isGone: boolean,
  currentScreen: string,
  elapsedMs: number,
): void {
  if (shouldShow && !isGone) {
    sendOverlayError(
      OVERLAY_ERROR.FADE_OUT_SCREEN_NOT_REACHED,
      `Overlay unmounted before reaching its fade-out screen — last screen: "${currentScreen}", elapsed: ${Math.round(elapsedMs)}ms`,
    );
  }
}

export interface ScreenDefinition {
  name: string;
  duration?: number; // ms — exact duration, screen is not flexible
  minDuration?: number; // ms — minimum required, will expand if time allows
  maxDuration?: number; // ms — cap for flex screens
}

export interface UseOverlayTimelineOptions {
  screens: ScreenDefinition[];
  maxOnScreenDuration?: number; // ms — hard cap on total overlay time (default 45s)
  fallbackSlotDuration?: number; // ms — used when SDK doesn't respond (default 60s)
  sdkTimeoutMs?: number; // how long to wait for SDK duration response
}

export function useOverlayTimeline(options: UseOverlayTimelineOptions) {
  const {
    screens,
    maxOnScreenDuration = 45000,
    fallbackSlotDuration = 60000,
    sdkTimeoutMs = 2000,
  } = options;

  const totalOnScreenDuration = ref<number>(0);
  const elapsedMs = ref<number>(0);
  const currentScreenIndex = ref<number>(0);
  const shouldShow = ref<boolean>(true);
  let isRealEnv = false;

  let spoofInterval: ReturnType<typeof setInterval> | null = null;
  let lastSpoofTick: number = 0;

  // Total of all fixed-duration screens
  const fixedTotal = screens.reduce((sum, s) => sum + (s.duration || 0), 0);

  // Total minimum time required across all flex screens
  const flexMinTotal = screens.reduce((sum, s) => sum + (s.minDuration || 0), 0);

  // Minimum total time needed to show all screens at their minimum
  const minimumRequiredMs = fixedTotal + flexMinTotal;

  // Compute the actual durations for each screen based on available time
  const screenDurations = computed(() => {
    const total = Math.min(totalOnScreenDuration.value, maxOnScreenDuration);
    const availableForFlex = Math.max(0, total - fixedTotal);

    // Distribute available flex time respecting per-screen max caps
    let remaining = availableForFlex;
    const flexDurations = new Map<number, number>();

    // First pass: assign minimums
    for (let i = 0; i < screens.length; i++) {
      if (screens[i].minDuration) {
        flexDurations.set(i, screens[i].minDuration!);
        remaining -= screens[i].minDuration!;
      }
    }

    // Second pass: distribute extra time equally, respecting maxDuration caps
    let uncappedIndices = [...flexDurations.keys()].filter((i) => {
      const max = screens[i].maxDuration;
      return !max || flexDurations.get(i)! < max;
    });

    while (remaining > 0 && uncappedIndices.length > 0) {
      const share = remaining / uncappedIndices.length;
      let distributed = 0;
      const stillUncapped: number[] = [];

      for (const i of uncappedIndices) {
        const current = flexDurations.get(i)!;
        const max = screens[i].maxDuration;
        const canAdd = max ? Math.min(share, max - current) : share;
        flexDurations.set(i, current + canAdd);
        distributed += canAdd;
        if (!max || flexDurations.get(i)! < max) {
          stillUncapped.push(i);
        }
      }

      remaining -= distributed;
      uncappedIndices = stillUncapped;
      if (distributed < 0.1) break;
    }

    return screens.map((s, i) => {
      if (s.duration) return s.duration;
      if (s.minDuration) return flexDurations.get(i) || s.minDuration;
      return 0;
    });
  });

  const screenThresholds = computed(() => {
    const durations = screenDurations.value;
    const thresholds: number[] = [];
    let accumulated = 0;

    for (let i = 0; i < screens.length; i++) {
      thresholds.push(accumulated);
      accumulated += durations[i];
    }
    return thresholds;
  });

  const currentScreen = computed(() => screens[currentScreenIndex.value]?.name || '');

  const dismissScreen = screens.find((s) => s.name === 'dismiss');
  const dismissDurationMs = dismissScreen?.duration ?? 0;
  const isDismissing = ref(false);
  const isGone = ref(false);
  let dismissTimeout: ReturnType<typeof setTimeout> | null = null;

  function dismiss() {
    if (isDismissing.value) return;
    isDismissing.value = true;
    dismissTimeout = setTimeout(() => {
      isGone.value = true;
    }, dismissDurationMs);
  }

  watch(currentScreen, (newScreen, oldScreen) => {
    if (newScreen === 'dismiss') {
      dismiss();
    } else if (oldScreen === 'dismiss') {
      if (dismissTimeout) {
        clearTimeout(dismissTimeout);
        dismissTimeout = null;
      }
      isDismissing.value = false;
      isGone.value = false;
    }
  });

  watch(shouldShow, (val) => {
    isGone.value = !val;
  });

  function updateScreenFromElapsed() {
    const thresholds = screenThresholds.value;
    for (let i = thresholds.length - 1; i >= 0; i--) {
      if (elapsedMs.value >= thresholds[i]) {
        if (currentScreenIndex.value !== i) {
          currentScreenIndex.value = i;
          logDebug(
            `[useOverlayTimeline] Screen transition → "${screens[i].name}" at ${Math.round(elapsedMs.value)}ms`,
          );
        }
        break;
      }
    }
  }

  let lastSdkPositionMs: number = 0;

  function skipToScreen(screenName: string) {
    const idx = screens.findIndex((s) => s.name === screenName);
    if (idx === -1) {
      logError(`[useOverlayTimeline] Screen "${screenName}" not found`);
      return;
    }
    const threshold = screenThresholds.value[idx];
    elapsedMs.value = threshold;
    currentScreenIndex.value = idx;
    // Shift startOffset so that the last known SDK position maps to this threshold.
    // Subsequent SDK updates will advance the timeline forward from here.
    startOffset = lastSdkPositionMs - threshold;
    logDebug(
      `[useOverlayTimeline] Skipped to "${screenName}" (elapsed set to ${threshold}ms, startOffset adjusted to ${startOffset}ms)`,
    );
  }

  let slotDuration: number = 0;
  let startOffset: number = 0;
  let isInitialized = false;

  function handlePositionUpdate(positionMs: number) {
    if (slotDuration === 0) return;

    const remaining = slotDuration - positionMs;

    if (!isInitialized || !shouldShow.value) {
      if (remaining < minimumRequiredMs) {
        if (shouldShow.value) {
          shouldShow.value = false;
          logWarn(
            `[useOverlayTimeline] Slot too short: ${remaining}ms available, ${minimumRequiredMs}ms required`,
          );
          checkSlotDuration(remaining, minimumRequiredMs);
        }
        return;
      }
      isInitialized = true;
      startOffset = positionMs;
      totalOnScreenDuration.value = Math.min(remaining, maxOnScreenDuration);
      shouldShow.value = true;
      logDebug(
        `[useOverlayTimeline] Start offset: ${startOffset}ms, adjusted total: ${totalOnScreenDuration.value}ms`,
      );
    }

    lastSdkPositionMs = positionMs;
    elapsedMs.value = positionMs - startOffset;
    updateScreenFromElapsed();
  }

  function startSpoofTimer() {
    lastSpoofTick = performance.now();
    spoofInterval = setInterval(() => {
      const now = performance.now();
      const dt = now - lastSpoofTick;
      lastSpoofTick = now;
      elapsedMs.value += dt;
      updateScreenFromElapsed();
    }, 500);
  }

  function stopSpoofTimer() {
    if (spoofInterval) {
      clearInterval(spoofInterval);
      spoofInterval = null;
    }
  }

  async function fetchDuration(): Promise<number | null> {
    try {
      const result = await Promise.race([
        CommunicationUtil.getInContentOverlayDuration(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), sdkTimeoutMs)),
      ]);
      return typeof result === 'number' ? result : null;
    } catch (e) {
      logError('[useOverlayTimeline] getInContentOverlayDuration failed:', e);
      return null;
    }
  }

  let positionWatchdog: ReturnType<typeof setTimeout> | null = null;

  function setupSdkListener() {
    let firstPositionReceived = false;
    positionWatchdog = setTimeout(() => {
      if (!firstPositionReceived) {
        sendOverlayError(
          OVERLAY_ERROR.POSITION_UPDATES_STALLED,
          'No SDK position update received within 10s',
        );
      }
    }, 10_000);
    CommunicationUtil.onUpdateCurrentPosition = (positionMs: number) => {
      firstPositionReceived = true;
      handlePositionUpdate(positionMs);
    };
  }

  function teardownSdkListener() {
    CommunicationUtil.onUpdateCurrentPosition = undefined;
    if (positionWatchdog) clearTimeout(positionWatchdog);
  }

  // Non-SDK env (local dev / FULLMODE / BROWSER testing) — there's no real TV
  // parent or ad slot here, just a fallback duration, so a short "slot" isn't
  // a production signal worth alerting on. This only gates shouldShow;
  // it does NOT call checkSlotDuration/report SLOT_DURATION_BELOW_THRESHOLD.
  function evaluateVisibility() {
    const remaining = totalOnScreenDuration.value - elapsedMs.value;
    if (remaining < minimumRequiredMs) {
      shouldShow.value = false;
      logWarn(
        `[useOverlayTimeline] Slot too short: ${remaining}ms available, ${minimumRequiredMs}ms required`,
      );
    } else {
      shouldShow.value = true;
    }
  }

  onBeforeMount(async () => {
    const storeTracker = useTrackerStore();
    const storeBreak = useBreakStore();

    isRealEnv =
      !!storeTracker.isIframe &&
      !([BreakModeEnum.FULLMODE, BreakModeEnum.BROWSER] as (string | null)[]).includes(
        storeBreak.breakDisplayMode,
      );

    if (isRealEnv) {
      const sdkDuration = await fetchDuration();
      if (sdkDuration !== null) {
        slotDuration = sdkDuration;
        totalOnScreenDuration.value = sdkDuration;
        logInfo(`[useOverlayTimeline] SDK duration: ${sdkDuration}ms`);
      } else {
        slotDuration = fallbackSlotDuration;
        totalOnScreenDuration.value = fallbackSlotDuration;
        logInfo(`[useOverlayTimeline] SDK timeout, using fallback: ${fallbackSlotDuration}ms`);
      }
      // shouldShow stays true here — the overlay is already visually hidden by the
      // is-clear CSS class until assets load + fade-in delay (~500ms), which gives
      // the first SDK position update time to confirm visibility before anything renders.
    } else {
      slotDuration = fallbackSlotDuration;
      totalOnScreenDuration.value = Math.min(fallbackSlotDuration, maxOnScreenDuration);
      logInfo(
        `[useOverlayTimeline] Non-SDK env, using capped duration: ${totalOnScreenDuration.value}ms`,
      );
      evaluateVisibility();
    }
  });

  onMounted(() => {
    if (isRealEnv) {
      setupSdkListener();
    } else {
      if (!shouldShow.value) {
        logWarn('[useOverlayTimeline] Timeline not started — insufficient time');
        return;
      }
      startSpoofTimer();
    }

    logDebug(`[useOverlayTimeline] Timeline started, total: ${totalOnScreenDuration.value}ms`);
  });

  onBeforeUnmount(() => {
    // Non-SDK env (local dev / FULLMODE / BROWSER testing) — same reasoning as
    // evaluateVisibility()'s checkSlotDuration skip above: a dev closing/navigating
    // away from an overlay isn't a production signal worth alerting on.
    if (isRealEnv) {
      checkFadeOutReached(shouldShow.value, isGone.value, currentScreen.value, elapsedMs.value);
    }
    stopSpoofTimer();
    teardownSdkListener();
    if (dismissTimeout) {
      clearTimeout(dismissTimeout);
    }
  });

  function getScreenDuration(screenName: string): number {
    const idx = screens.findIndex((s) => s.name === screenName);
    if (idx === -1) return 0;
    return screenDurations.value[idx];
  }

  return {
    currentScreen,
    isDismissing,
    isGone,
    dismiss,
    skipToScreen,
    getScreenDuration,
  };
}
